import { Dayjs, isDayjs } from 'dayjs';

import isJson from '@/shared/utils/isJson';

type TransformMetadataForm = Record<string, unknown>;
/**
 * Transform metadata form to the format that the API expects
 *
 * Example:
 *
 * Input:
 * ```js
 * {
 *  'key1': 'value1',
 *  'key2': 'value2',
 *  'nested:key3': 'value3',
 *  'nested:key4': 'value4',
 * }
 * ```
 *
 * Output:
 * ```js
 * {
 *  'key1': 'value1',
 *  'key2': 'value2',
 *  'nested': {
 *    'key3': 'value3',
 *    'key4': 'value4',
 *   }
 * }
 * ```
 *
 * @param input - metadata form
 */
const transformMetadataForm = (
  input: TransformMetadataForm,
): TransformMetadataForm => {
  const output: TransformMetadataForm = {};
  const nestedKeys: { [prefix: string]: TransformMetadataForm } = {};

  for (const key in input) {
    if (key.includes(':')) {
      const [prefix, nestedKey] = key.split(':');

      if (!nestedKeys[prefix]) {
        nestedKeys[prefix] = {};
      }
      nestedKeys[prefix][nestedKey] = input[key];
    } else {
      if (isDayjs(input[key])) {
        output[key] = (input[key] as Dayjs).toISOString();
      } else if (isJson(input[key])) {
        try {
          output[key] = JSON.parse(input[key] as string) as object;
        } catch {
          output[key] = input[key] as string;
        }
      } else {
        output[key] = input[key] as string | number;
      }
    }
  }

  for (const prefix in nestedKeys) {
    output[prefix] = nestedKeys[prefix];
  }

  return output;
};

export default transformMetadataForm;
